import { Formik } from 'formik';
import { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { selectConfigurationParameterById } from '!core/redux';
import { TrlFileInput, TrlPill } from '»components/atoms';
import { TrlDatePicker, TrlTextField } from '»components/atoms/Inputs';
import { otpService } from '»components/Auth/Otp';
import { TrlFeedbackModal } from '»components/molecules/TrlFeedbackModal';
import { TrlFormButtons } from '»components/molecules/TrlFormButtons';
import { TrlFormRadioGroup } from '»components/molecules/TrlFormRadioGroup';
import { isSubmitFormButtonDisabled } from '»utils/form';
import { TrlFormattedMessagePlain } from '»utils/i18n';

import { CompletedProcessModal } from '../CommonComponents';
import { onboardingSlice } from '../redux';
import {
  onboardingDocumentsFeedback,
  onboardingDocumentsSelector,
  onboardingMainFormFieldsSelector,
  onboardingMainNameSelector,
  onboardingMandatoryAndIdentificationSelector,
  onboardingOpeningRequestIdSelector,
} from '../redux/onboarding.selectors';
import {
  FilesAttachmentContainer,
  Wrapper,
  WrapperRadioDate,
} from './FilesAttachment.styles';
import { filesAttachmentsValidation } from './FilesAttachments.yup';

export const FilesAttachment = () => {
  const dispatch = useDispatch();

  const formikRef = useRef(null);

  const name = useSelector(onboardingMainNameSelector);
  const openingRequestId = useSelector(onboardingOpeningRequestIdSelector);
  const mainFormFields = useSelector(onboardingMainFormFieldsSelector);
  const documents = useSelector(onboardingDocumentsSelector);
  const feedback = useSelector(onboardingDocumentsFeedback);
  const mandatoryAndIdentification = useSelector(
    onboardingMandatoryAndIdentificationSelector,
  );
  const hasOnboardingFinalOtp =
    useSelector((state) =>
      selectConfigurationParameterById(state, 'ONBOARDING_OTP_END'),
    )?.value === 'Y';
  const onboardingFinalOtpDelivMetds = useSelector((state) =>
    selectConfigurationParameterById(
      state,
      'ONBOARDING_OTP_DELIVERY_METHODS_END',
    ),
  ).value.split(';');

  const hasOneOtpDelivMetd = onboardingFinalOtpDelivMetds.length === 1;

  const formIdDocCode = mainFormFields.find(
    (field) => field.name === 'idType',
  )?.value;
  const formIdDocNumber = mainFormFields.find(
    (field) => field.name === 'idNumber',
  )?.value;
  const formIdDocExpDate = mainFormFields.find(
    (field) => field.name === 'idExpDate',
  )?.value;

  const yesLabel = TrlFormattedMessagePlain({
    id: 'TRLT_UI-YES',
  });

  const noLabel = TrlFormattedMessagePlain({
    id: 'TRLT_UI-NO',
  });

  const getDocDescr = (currentDocCode) =>
    documents.find((doc) => doc.documentCode === currentDocCode).description;

  const handleSubmit = (values, { setSubmitting }) => {
    const disableFormSubmittingState = () => {
      setSubmitting(false);
    };

    const identificationDocs = Object.values(values).filter((value) => {
      return value.identification === 'S';
    });

    const hasValidIdentificationDoc = identificationDocs.some(
      (value) => value.validated === true,
    );

    if (!hasValidIdentificationDoc) {
      const invalidIdentificationDocIndex = identificationDocs.findIndex(
        (value) => value.validated === false && value.documentNumber !== '',
      );

      const keyOfInvalidIdentificationDoc = Object.entries(values).find(
        ([__dirname, v]) =>
          v.identification === 'S' &&
          v.validated === false &&
          v.documentNumber !== '',
      )[0];

      disableFormSubmittingState();
      dispatch(
        onboardingSlice.actions.submitDocumentFailure({
          index: invalidIdentificationDocIndex,
          error: 'TRLT_UI-ATTACH_DOC_ERROR_DESC',
          filename: values[keyOfInvalidIdentificationDoc].file?.name,
          fieldName: getDocDescr(keyOfInvalidIdentificationDoc),
          key: keyOfInvalidIdentificationDoc,
        }),
      );
      return;
    }

    const invalidMandatoryDoc = Object.values(values).find((value) => {
      return value.mandatory === 'S' && value.validated === false;
    });

    if (invalidMandatoryDoc) {
      const invalidMandatoryDocIndex = Object.values(values).findIndex(
        (value) => {
          return value.mandatory === 'S' && value.validated === false;
        },
      );

      const keyOfInvalidMandatoryDoc = Object.entries(values).find(
        ([__dirname, v]) => v.mandatory === 'S' && v.validated === false,
      )[0];

      disableFormSubmittingState();
      dispatch(
        onboardingSlice.actions.submitDocumentFailure({
          index: invalidMandatoryDocIndex,
          error: 'TRLT_UI-ATTACH_DOC_ERROR_DESC',
          filename: values[keyOfInvalidMandatoryDoc].file?.name,
          fieldName: getDocDescr(keyOfInvalidMandatoryDoc),
          key: keyOfInvalidMandatoryDoc,
        }),
      );
      return;
    }

    if (hasOnboardingFinalOtp) {
      if (hasOneOtpDelivMetd) {
        const otpDelivMetd = onboardingFinalOtpDelivMetds[0];

        otpService
          .getOTPOnboarding({
            openingRequestId,
            entityName: name,
            otpSendType: otpDelivMetd,
            onboardingState: 'END',
          })
          .then(() => {
            dispatch(
              onboardingSlice.actions.setOnboardingCurrentStep({
                direction: 'forward',
              }),
            );
          })
          .catch((error) => {
            dispatch(onboardingSlice.actions.setOnboardingStateFailure(error));
            disableFormSubmittingState();
          });
      } else {
        dispatch(
          onboardingSlice.actions.setOnboardingCurrentStep({
            direction: 'forward',
          }),
        );
      }
    } else {
      dispatch(
        onboardingSlice.actions.setOnboardingStateRequest({
          openingRequestId,
          state: 'ENDOK',
          disableFormSubmittingState,
        }),
      );
    }
  };

  const initialValues = documents.reduce((acc, doc) => {
    const isIdDoc = doc.idType === formIdDocCode;

    if (doc.values?.file) {
      acc[doc.documentCode] = {
        file: doc.values.file,
        documentNumber: doc.values.docNumber,
        lifetime: doc.values.lifetime,
        expirationDate: doc.values.expirationDate,
        validated: doc.values.validated,
        identification: doc.identification,
        mandatory: doc.mandatory,
        numberMandatory: doc.NumberMandatory,
      };
    } else {
      acc[doc.documentCode] = {
        file: formikRef.current?.values[doc.documentCode].file ?? null,
        documentNumber:
          formikRef.current?.values[doc.documentCode].documentNumber ??
          (isIdDoc && formIdDocNumber ? formIdDocNumber : '') ??
          '',
        lifetime:
          formikRef.current?.values[doc.documentCode].lifetime ??
          (isIdDoc && formIdDocExpDate && 'N') ??
          '',
        expirationDate:
          formikRef.current?.values[doc.documentCode].expirationDate ??
          (isIdDoc && formIdDocExpDate ? formIdDocExpDate : '') ??
          '',
        validated:
          formikRef.current?.values[doc.documentCode].validated ?? false,
        identification: doc.identification,
        mandatory: doc.mandatory,
        numberMandatory: doc.NumberMandatory,
      };
    }
    return acc;
  }, {});

  const checkIfAnyIdentificationIsFilled = ({ values }) => {
    const identificationDocs = Object.values(values).filter((value) => {
      return value.identification === 'S';
    });

    const hasValidIdentificationDoc = identificationDocs.some(
      (value) =>
        ((value.numberMandatory === 'S' && value.documentNumber !== '') ||
          value.numberMandatory === 'N') &&
        (value.lifetime === 'S' ||
          (value.lifetime === 'N' && value.expirationDate !== '')) &&
        value.file !== null,
    );

    if (!hasValidIdentificationDoc) {
      return true;
    }
    return false;
  };

  const getTitleContent = (isDocumentNotValidated) => {
    if (feedback?.type === 'error') {
      if (isDocumentNotValidated) {
        return 'TRLT_UI-FILES_NOT_SUBMITTED';
      }
      return 'TRLT_UI-ATTACH_DOC_ERROR';
    }
    return 'TRLT_UI-ATTACH_DOC_SUCCESS';
  };
  const getDescrContent = (isDocumentNotValidated) => {
    if (feedback?.type === 'error') {
      if (isDocumentNotValidated) {
        return 'TRLT_UI-MUST_SUBMIT_FILES';
      }
      return 'TRLT_UI-ATTACH_DOC_ERROR_DESC';
    }
    return 'TRLT_UI-ATTACH_DOC_SUCCESS_DESC';
  };

  const getValueName = (isDocumentNotValidated) => {
    const fileText = `${feedback?.filename} (${feedback?.fieldName})`;

    if (feedback?.type === 'error' && isDocumentNotValidated)
      return { files: fileText };

    return { fileName: fileText };
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={filesAttachmentsValidation(documents)}
      validateOnMount
      innerRef={formikRef}
    >
      {({ values, isSubmitting, dirty, errors, setFieldValue }) => {
        const canAdvance =
          checkIfAnyIdentificationIsFilled({ values }) ||
          isSubmitFormButtonDisabled({
            isSubmitting,
            dirty,
            errors,
            values,
            areFiles: true,
          });

        const isDocumentNotValidated =
          values[feedback?.key] && !values[feedback?.key].validated;

        return (
          <FilesAttachmentContainer className="formContainer">
            {documents.map((document, index) => {
              const isIdDocument = document.identification === 'S';
              const isIdDocumentNotMandatory =
                !mandatoryAndIdentification && isIdDocument;
              const isDocumentNumberFieldRequired =
                document.mandatory === 'S' && document.NumberMandatory === 'S';
              const { documentCode } = document;
              const { lifetime, expirationDate, file, documentNumber } =
                values[documentCode];

              const canSubmitDoc =
                (errors[documentCode]
                  ? Object.values(errors[documentCode]).length === 0
                  : true) &&
                file &&
                (lifetime === 'S' ||
                  (lifetime === 'N' && expirationDate !== ''));

              return (
                <Wrapper key={document.id}>
                  <TrlFileInput
                    index={index}
                    documentRecord={document}
                    name={`${document.documentCode}.file`}
                    isIdDocument={isIdDocument}
                    isIdDocumentNotMandatory={isIdDocumentNotMandatory}
                  />
                  <TrlTextField
                    name={`${document.documentCode}.documentNumber`}
                    type="text"
                    label={TrlFormattedMessagePlain({
                      id: 'TRLT_UI-DOCUMENT_NUMBER',
                    })}
                    isRequired={
                      isDocumentNumberFieldRequired || isIdDocumentNotMandatory
                    }
                    disabled={document.NumberMandatory === 'N'}
                    placeholder={TrlFormattedMessagePlain({
                      id: 'TRLT_UI-DOCUMENT_NUMBER',
                    })}
                    id="documentNumber"
                    additionalFunc={() => {
                      if (values[document.documentCode].validated) {
                        setFieldValue(
                          `${document.documentCode}.validated`,
                          false,
                          false,
                        );
                      }
                    }}
                  />
                  <WrapperRadioDate>
                    <TrlFormRadioGroup
                      name={`${document.documentCode}.lifetime`}
                      label={TrlFormattedMessagePlain({
                        id: 'TRLT_UI-LIFETIME',
                      })}
                      options={[
                        { label: yesLabel, id: 'S' },
                        { label: noLabel, id: 'N' },
                      ]}
                      noPadding
                      filesPage
                      isRequired={
                        document.mandatory === 'S' || isIdDocumentNotMandatory
                      }
                      additionalFunc={() => {
                        if (values[document.documentCode].validated) {
                          setFieldValue(
                            `${document.documentCode}.validated`,
                            false,
                            false,
                          );
                        }
                      }}
                    />
                    <TrlDatePicker
                      name={`${document.documentCode}.expirationDate`}
                      label={TrlFormattedMessagePlain({
                        id: 'TRLT_UI-EXPIRATION_DATE_LONG',
                      })}
                      noPadding
                      isRequired={
                        document.mandatory === 'S' || isIdDocumentNotMandatory
                      }
                      isDisabled={
                        values[document.documentCode].lifetime === 'S'
                      }
                      disablePast
                      value={values[document.documentCode].expirationDate}
                      dateFormatType="date"
                    />
                  </WrapperRadioDate>
                  <TrlPill
                    label={TrlFormattedMessagePlain({
                      id: 'TRLT_UI-SUBMIT_DOCUMENT',
                    })}
                    variant="addItem"
                    disabled={!canSubmitDoc}
                    onClick={() => {
                      dispatch(
                        onboardingSlice.actions.submitDocumentRequest({
                          openingRequestId,
                          documentCode: document.documentCode,
                          deleteDoc: 'N',
                          lifetime,
                          expirationDate,
                          docNumber: documentNumber,
                          index,
                          file,
                          setFieldValue,
                          webDescription: document.webDescription,
                          description: document.description,
                          identification: document.identification,
                          mandatory: document.mandatory,
                          key: index,
                        }),
                      );
                    }}
                  />
                </Wrapper>
              );
            })}
            <TrlFeedbackModal
              title={{
                content: getTitleContent(isDocumentNotValidated),
              }}
              descr={{
                content: getDescrContent(isDocumentNotValidated),
                values: getValueName(isDocumentNotValidated),
              }}
              type={feedback?.type}
              open={feedback?.isOpen ?? false}
              onClose={() => {
                dispatch(onboardingSlice.actions.clearDocumentFeedback());
              }}
            />
            <CompletedProcessModal />
            <TrlFormButtons
              fixedAtBottom
              advanceButtonName="TRLT_UI-NEXT"
              isAdvanceButtonDisabled={canAdvance}
            />
          </FilesAttachmentContainer>
        );
      }}
    </Formik>
  );
};
